import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import api from "@/lib/apiClient";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | error | expired
  const [items, setItems] = useState([]);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setItems(data.items || []);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
      } catch (e) { console.debug("payment status poll retry", e); }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="w-14 h-14 animate-spin text-blue-700 mx-auto mb-5" /><h1 className="font-display text-2xl font-bold text-blue-950 dark:text-blue-50">Confirming your payment…</h1><p className="mt-2 text-slate-500">This only takes a moment.</p></>)}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-5" />
          <h1 className="font-display text-3xl font-bold text-blue-950 dark:text-blue-50">Payment successful!</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">Thank you for your purchase. Your downloads are ready in your portal.</p>
          {items.length > 0 && (
            <div className="mt-6 text-left bg-white dark:bg-blue-900/30 rounded-2xl p-5 border border-blue-900/5 space-y-3">
              {items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between">
                  <span className="text-sm text-blue-950 dark:text-blue-50">{it.name}</span>
                  {it.download_url && <a href={it.download_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-amber-700 font-medium"><Download className="w-4 h-4" /> Download</a>}
                </div>
              ))}
            </div>
          )}
          <div className="mt-7 flex gap-3 justify-center">
            <Button asChild className="bg-blue-900 hover:bg-blue-950 text-white h-12 px-7"><Link to="/dashboard" data-testid="success-portal-link">Go to My Portal</Link></Button>
            <Button asChild variant="outline" className="h-12 px-7"><Link to="/store">Keep shopping</Link></Button>
          </div>
        </>
      )}
      {(state === "error" || state === "expired") && (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-5" />
          <h1 className="font-display text-2xl font-bold text-blue-950 dark:text-blue-50">{state === "expired" ? "Checkout session expired" : "We couldn't confirm your payment"}</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">If you were charged, your downloads will appear in your portal shortly. Otherwise please try again.</p>
          <Button asChild className="mt-7 bg-blue-900 text-white h-12 px-7"><Link to="/store">Back to store</Link></Button>
        </>
      )}
    </div>
  );
}
